import { useEffect, useState } from 'react';

import { fetchGetIwocapayPaymentLater } from '@iwoca/lapi-client/edge';
import { useQueryClient } from '@tanstack/react-query';

import { useSuspenseStateKey } from '../../../hooks/useStateKey.hook';
import {
  clearPostDecisionQueries,
  MAX_POLL_ATTEMPTS,
  POLL_INTERVAL_MS,
  runDecisionPolling,
} from '../../PayLinkLanding/utils/decisionPolling';

/**
 * After checkout, the backend may still be processing. This hook blocks
 * the dashboard until the backend state has settled.
 *
 * Two modes:
 * - **funding** (default): polls until the paylink is funded or has
 *   capture_delay_hours. Used after the normal drawdown flow.
 * - **decision** (`awaitDecision`): formalises an open application first
 *   (one-shot), then polls until a valid formal offer appears. Used for
 *   repeat-customer redirects where the backend is re-decisioning.
 *
 * Once settled, stale query caches are cleared so downstream suspense
 * queries refetch fresh data.
 */
export function useAwaitPostCheckout(
  payLinkId: string,
  { awaitDecision = false }: { awaitDecision?: boolean } = {},
) {
  const { stateKey } = useSuspenseStateKey();
  const queryClient = useQueryClient();
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;
    let cancelled = false;

    function settle() {
      clearPostDecisionQueries(queryClient);
      if (!cancelled) setIsReady(true);
    }

    function delay(): Promise<void> {
      return new Promise((resolve) => {
        timeoutId = setTimeout(resolve, POLL_INTERVAL_MS);
      });
    }

    async function pollUntil(check: () => Promise<boolean>) {
      for (let attempt = 0; attempt < MAX_POLL_ATTEMPTS; attempt++) {
        if (cancelled) return;

        try {
          if (await check()) {
            settle();
            return;
          }
        } catch {
          // Network errors — retry on next attempt
        }

        await delay();
      }

      // Max attempts reached — unblock the dashboard
      settle();
    }

    async function awaitFunding() {
      await pollUntil(async () => {
        const response = await fetchGetIwocapayPaymentLater({
          stateKey: stateKey!,
        });
        const payLink = response.data?.find((pl) => pl.id === payLinkId);
        return !!payLink?.is_funded || !!payLink?.capture_delay_hours;
      });
    }

    async function awaitDecisionFlow() {
      const outcome = await runDecisionPolling({
        stateKey: stateKey!,
        isCancelled: () => cancelled,
      });

      if (outcome === null) return;
      settle();
    }

    void (awaitDecision ? awaitDecisionFlow() : awaitFunding());

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [payLinkId, stateKey, queryClient, awaitDecision]);

  return { isReady };
}
